import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CustomerProfileView } from '@/components/shared/CustomerProfileView';
import { TopUpDialog } from '@/components/staff/TopUpDialog';
import { BonusTiffinsDialog } from '@/components/staff/BonusTiffinsDialog';
import { toast } from 'sonner';
import { KeyRound, Wallet, Gift } from 'lucide-react';

export default function CustomerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetCode, setResetCode] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);

  const load = () => staffApi.get(`/customers/${id}`).then(({ data }) => setData(data));

  useEffect(() => {
    load();
  }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await staffApi.post(`/customers/${id}/notes`, { note });
      setNote('');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const generateResetCode = async () => {
    setResetting(true);
    try {
      const { data } = await staffApi.post(`/customers/${id}/reset-password`);
      setResetCode(data.code);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = (open) => {
    setResetOpen(open);
    if (!open) setResetCode(null);
  };

  if (!data) return <p className="text-muted-ink text-sm">Loading customer…</p>;
  const { customer, redemptions, payments } = data;
  const activeSubscription = customer.subscriptions.find((s) => s.status === 'ACTIVE');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-700 text-charcoal" data-testid="customer-profile-name">
            {customer.name}
          </h1>
          <p className="text-sm text-muted-ink">
            {customer.customerCode} · {customer.phone} · {customer.email}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {activeSubscription && (
            <>
              <Button
                variant="outline"
                data-testid="open-staff-topup-btn"
                onClick={() => setTopUpOpen(true)}
                className="gap-2 border-coriander text-coriander hover:bg-coriander/10"
              >
                <Wallet size={15} /> Top Up
              </Button>
              <TopUpDialog
                open={topUpOpen}
                onOpenChange={setTopUpOpen}
                subscriptionId={activeSubscription.id}
                currentPlanId={activeSubscription.plan?.id}
                customerName={customer.name}
                onDone={() => {
                  setTopUpOpen(false);
                  load();
                }}
              />
              <Button
                variant="outline"
                data-testid="open-bonus-tiffins-btn"
                onClick={() => setBonusOpen(true)}
                className="gap-2 border-saffron text-saffron hover:bg-saffron/10"
              >
                <Gift size={15} /> Bonus Tiffins
              </Button>
              <BonusTiffinsDialog
                open={bonusOpen}
                onOpenChange={setBonusOpen}
                subscriptionId={activeSubscription.id}
                customerName={customer.name}
                onDone={() => {
                  setBonusOpen(false);
                  load();
                }}
              />
            </>
          )}
          <Dialog open={resetOpen} onOpenChange={openResetDialog}>
          <Button
            variant="outline"
            data-testid="reset-password-btn"
            onClick={() => openResetDialog(true)}
            className="shrink-0 gap-2"
          >
            <KeyRound size={15} /> Reset Password
          </Button>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset {customer.name}&apos;s password</DialogTitle>
              <DialogDescription>
                This generates a one-time 4-digit code, valid for 15 minutes. Tell it to the customer directly —
                they&apos;ll enter it (with their email) at thetiffin.app/reset-with-code to set a new password.
              </DialogDescription>
            </DialogHeader>
            {resetCode ? (
              <div className="text-center py-4">
                <p className="text-xs text-muted-ink mb-2">Share this code with the customer</p>
                <p className="font-heading text-5xl font-900 tracking-[0.3em] text-charcoal" data-testid="reset-password-code">
                  {resetCode}
                </p>
                <p className="text-xs text-muted-ink mt-3">Expires in 15 minutes · one-time use</p>
              </div>
            ) : (
              <DialogFooter>
                <Button
                  data-testid="reset-password-generate-btn"
                  disabled={resetting}
                  onClick={generateResetCode}
                  className="w-full bg-saffron hover:bg-saffron-hover"
                >
                  {resetting ? 'Generating…' : 'Generate Code'}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
          </Dialog>
        </div>
      </div>


      <CustomerProfileView
        customer={customer}
        redemptions={redemptions}
        payments={payments}
        linkSubscriptions
        noteInput={note}
        onNoteInputChange={setNote}
        onAddNote={addNote}
        savingNote={savingNote}
      />
    </div>
  );
}
